import React, { useEffect, useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LenderApp from "./lender/App";
import BorrowerApp from "./borrower/App";
import ErrorBoundary from "./shared/components/ErrorBoundary";
import { enforceRoleAccess, getUserRole } from "./shared/utils/roleManager";
import { ThemeProvider } from "./shared/contexts/ThemeContext";
import "./lender/index.css";

const getViewFromPath = () => (window.location.pathname.startsWith("/borrower") ? "borrower" : "lender");

const App = () => {
  const [view, setView] = useState(getViewFromPath());
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Enforce role-based access control BEFORE rendering
  useEffect(() => {
    const currentPath = window.location.pathname;
    const role = getUserRole();
    const isBorrowerPath = currentPath.startsWith("/borrower");

    console.log("[App.js Auth Check]", {
      currentPath,
      role,
      isBorrowerPath,
      shouldBlock: isBorrowerPath && !role
    });

    // If trying to access borrower portal without authentication, redirect immediately
    if (isBorrowerPath && !role) {
      console.warn("Unauthenticated access to borrower portal blocked - redirecting");
      window.location.href = "/";
      return;
    }

    // Run the full role access enforcement
    enforceRoleAccess(currentPath);
    setIsAuthChecked(true);
  }, []);

  // Re-check on view changes
  useEffect(() => {
    if (isAuthChecked) {
      enforceRoleAccess(window.location.pathname);
    }
  }, [view, isAuthChecked]);

  useEffect(() => {
    document.body.dataset.appView = view;
    return () => {
      delete document.body.dataset.appView;
    };
  }, [view]);

  useEffect(() => {
    const handlePop = () => setView(getViewFromPath());
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const switchView = (next) => {
    setView(next);
    const path = next === "borrower" ? "/borrower" : "/";
    window.history.pushState({}, "", path);
  };

  const isBorrower = view === "borrower";

  // Don't render borrower app until auth is checked
  if (isBorrower && !isAuthChecked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Checking authentication...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 transition-colors duration-300">
          {isBorrower ? (
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
              <BorrowerApp />
            </GoogleOAuthProvider>
          ) : (
            <LenderApp />
          )}
        </div>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
